/**
 * TabBar — horizontal tab management bar for editor state tabs.
 *
 * Shows all open tabs as compact chips. Active tab is highlighted.
 * Tabs are draggable for reordering via dnd-kit.
 * Double-click tab name to rename. "+" creates new tab. "X" closes tab.
 */

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Plus,X } from 'lucide-react';
import React, { useCallback, useEffect,useRef, useState } from 'react';

import { cn } from '@/lib/utils';
import { useEditorStore } from '@/store/useEditorStore';
import { usePipelineStore } from '@/store/usePipelineStore';
import { useTabStore } from '@/store/useTabStore';

// ---------------------------------------------------------------------------
// SortableTab
// ---------------------------------------------------------------------------

const SortableTab: React.FC<{
  tabId: string;
  name: string;
  isActive: boolean;
  onSwitch: (tabId: string) => void;
  onClose: (tabId: string) => void;
  onRename: (tabId: string, name: string) => void;
}> = ({ tabId, name, isActive, onSwitch, onClose, onRename }) => {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(name);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tabId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : undefined,
  };

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const commitRename = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== name) {
      onRename(tabId, trimmed);
    }
    setEditing(false);
  };

  const cancelRename = () => {
    setEditValue(name);
    setEditing(false);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'group flex items-center gap-1 px-2 py-0.5 rounded-[7px] text-[11px] cursor-pointer',
        'transition-all duration-150 min-w-0 border select-none',
        isActive
          ? 'border-violet-400 text-foreground bg-violet-400/[0.08]'
          : 'border-border bg-muted/50 text-muted-foreground hover:border-zinc-500 hover:text-foreground'
      )}
      onClick={() => onSwitch(tabId)}
      {...attributes}
      {...listeners}
    >
      {editing ? (
        <input
          ref={inputRef}
          className="font-medium text-[11px] bg-transparent border-none outline-none w-[80px] min-w-0 p-0"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitRename();
            if (e.key === 'Escape') cancelRename();
            e.stopPropagation();
          }}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        />
      ) : (
        <span
          className="font-medium truncate max-w-[120px]"
          onDoubleClick={(e) => {
            e.stopPropagation();
            setEditValue(name);
            setEditing(true);
          }}
        >
          {name}
        </span>
      )}
      <button
        className="h-3 w-3 rounded-sm opacity-60 text-muted-foreground hover:text-rose-400 flex items-center justify-center transition-opacity shrink-0"
        onClick={(e) => {
          e.stopPropagation();
          onClose(tabId);
        }}
        onPointerDown={(e) => e.stopPropagation()}
        aria-label={`Close ${name}`}
        title={`Close ${name}`}
      >
        <X className="h-2 w-2" />
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// TabBar
// ---------------------------------------------------------------------------

export const TabBar: React.FC<{ onCloseTab?: (tabId: string) => void }> = ({ onCloseTab }) => {
  const tabs = useTabStore((s) => s.tabs);
  const activeTabId = useTabStore((s) => s.activeTabId);
  const switchTab = useTabStore((s) => s.switchTab);
  const defaultCloseTab = useTabStore((s) => s.closeTab);
  const createTab = useTabStore((s) => s.createTab);
  const reorderTabs = useTabStore((s) => s.reorderTabs);
  const renameTab = useTabStore((s) => s.renameTab);

  const handleRename = useCallback((tabId: string, newName: string) => {
    renameTab(tabId, newName);

    // Sync rename to saved pipeline — prefer live appStore for active tab
    const isActiveTab = tabId === useTabStore.getState().activeTabId;
    const pipelineId = isActiveTab
      ? useEditorStore.getState().activePipelineId
      : useTabStore.getState().tabs.find((t) => t.id === tabId)?.activePipelineId;

    if (pipelineId) {
      usePipelineStore.getState().updatePipeline(pipelineId, { name: newName });
    }

    // Keep appStore in sync if this is the active tab
    if (isActiveTab) {
      useEditorStore.getState().setActivePipelineName(newName);
    }
  }, [renameTab]);

  const closeTab = onCloseTab ?? defaultCloseTab;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = tabs.findIndex((t) => t.id === active.id);
      const newIndex = tabs.findIndex((t) => t.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      reorderTabs(oldIndex, newIndex);
    },
    [tabs, reorderTabs]
  );

  return (
    <div className="h-[30px] flex items-center gap-1 px-3 border-b border-border bg-background shrink-0 overflow-hidden">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={tabs.map((t) => t.id)}
          strategy={horizontalListSortingStrategy}
        >
          {tabs.map((tab) => (
            <SortableTab
              key={tab.id}
              tabId={tab.id}
              name={tab.name}
              isActive={tab.id === activeTabId}
              onSwitch={switchTab}
              onClose={closeTab}
              onRename={handleRename}
            />
          ))}
        </SortableContext>
      </DndContext>

      <button
        className="h-5 px-2 text-[10px] rounded-[7px] border border-dashed border-zinc-500 text-muted-foreground hover:border-violet-400 hover:text-violet-400 transition-all duration-150 shrink-0 ml-1"
        title="New tab"
        onClick={() => createTab()}
      >
        <Plus className="h-3 w-3" />
      </button>
    </div>
  );
};
